import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Check, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import type { AdrCommentNode } from '../../types';
import { useEmployeeById, useEmployees } from '../../stores';

// ============================================
// CommentNodeView — Recursive comment node
// ============================================

function CommentNodeView({
  node,
  depth,
  onReply,
  onResolve,
  onUnresolve,
}: {
  node: AdrCommentNode;
  depth: number;
  onReply: (parentId: string, text: string, authorEmployeeId: string) => void;
  onResolve: (commentId: string) => void;
  onUnresolve: (commentId: string) => void;
}) {
  const { t } = useTranslation();
  const author = useEmployeeById(node.authorEmployeeId);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyAuthor, setReplyAuthor] = useState('');
  const [collapsed, setCollapsed] = useState(node.resolved);
  const employees = useEmployees();

  const handleSubmitReply = () => {
    if (!replyText.trim() || !replyAuthor) return;
    onReply(node.id, replyText.trim(), replyAuthor);
    setReplyText('');
    setReplyOpen(false);
  };

  const isResolved = node.resolved;

  return (
    <div style={{ marginLeft: depth > 0 ? 16 : 0 }}>
      <div
        style={{
          padding: '6px 8px',
          borderRadius: 4,
          backgroundColor: isResolved ? '#f1f5f9' : '#fff',
          border: `1px solid ${isResolved ? '#cbd5e1' : '#d1d5db'}`,
          opacity: isResolved ? 0.6 : 1,
          marginBottom: 4,
          boxShadow: isResolved ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {node.children.length > 0 && (
            <button onClick={() => setCollapsed(!collapsed)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
              {collapsed ? <ChevronRight size={10} color="#94a3b8" /> : <ChevronDown size={10} color="#94a3b8" />}
            </button>
          )}
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: author?.color || '#9ca3af',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 10, fontWeight: 500, color: '#374151' }}>
            {author?.name || node.authorEmployeeId}
          </span>
          <span style={{ fontSize: 9, color: '#9ca3af' }}>
            {new Date(node.createdAt).toLocaleDateString()}
          </span>
          {isResolved && (
            <span style={{ fontSize: 9, color: '#10b981', fontWeight: 500 }}>
              {t('adr.comment.resolved')}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#374151', whiteSpace: 'pre-wrap' }}>{node.text}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            onClick={() => setReplyOpen(!replyOpen)}
            style={{ fontSize: 9, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: 0 }}
          >
            <MessageSquare size={9} /> {t('adr.comment.reply')}
          </button>
          {isResolved ? (
            <button
              onClick={() => onUnresolve(node.id)}
              style={{ fontSize: 9, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: 0 }}
            >
              <RotateCcw size={9} /> {t('adr.comment.unresolve')}
            </button>
          ) : (
            <button
              onClick={() => onResolve(node.id)}
              style={{ fontSize: 9, color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, padding: 0 }}
            >
              <Check size={9} /> {t('adr.comment.resolve')}
            </button>
          )}
        </div>

        {replyOpen && (
          <div style={{ marginTop: 6 }}>
            <select
              value={replyAuthor}
              onChange={(e) => setReplyAuthor(e.target.value)}
              style={{ width: '100%', fontSize: 10, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 4, backgroundColor: '#fff' }}
            >
              <option value="">{t('adr.person.searchEmployee')}</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={t('adr.comment.placeholder')}
              style={{ width: '100%', fontSize: 10, padding: 4, border: '1px solid #d1d5db', borderRadius: 4, minHeight: 40, resize: 'vertical', backgroundColor: '#fff' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmitReply();
                }
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginTop: 4 }}>
              <button
                onClick={() => setReplyOpen(false)}
                style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmitReply}
                disabled={!replyText.trim() || !replyAuthor}
                style={{ fontSize: 10, padding: '2px 8px', border: 'none', borderRadius: 4, background: '#3b82f6', color: '#fff', cursor: 'pointer', opacity: (!replyText.trim() || !replyAuthor) ? 0.4 : 1 }}
              >
                {t('adr.comment.reply')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Children */}
      {!collapsed && node.children.map(child => (
        <CommentNodeView
          key={child.id}
          node={child}
          depth={depth + 1}
          onReply={onReply}
          onResolve={onResolve}
          onUnresolve={onUnresolve}
        />
      ))}
    </div>
  );
}

// ============================================
// AdrCommentTree
// ============================================

export const AdrCommentTree = memo(function AdrCommentTree({
  comments,
  onAddComment,
  onResolve,
  onUnresolve,
}: {
  comments: AdrCommentNode[];
  onAddComment: (text: string, authorEmployeeId: string, parentCommentId: string | null) => void;
  onResolve: (commentId: string) => void;
  onUnresolve: (commentId: string) => void;
}) {
  const { t } = useTranslation();
  const employees = useEmployees();
  const [newText, setNewText] = useState('');
  const [newAuthor, setNewAuthor] = useState('');

  const handleSubmit = () => {
    if (!newText.trim() || !newAuthor) return;
    onAddComment(newText.trim(), newAuthor, null);
    setNewText('');
  };

  const handleReply = (parentId: string, text: string, authorEmployeeId: string) => {
    onAddComment(text, authorEmployeeId, parentId);
  };

  return (
    <div>
      {comments.map(node => (
        <CommentNodeView
          key={node.id}
          node={node}
          depth={0}
          onReply={handleReply}
          onResolve={onResolve}
          onUnresolve={onUnresolve}
        />
      ))}

      {/* New root comment */}
      <div style={{ marginTop: 8, padding: 8, border: '1px solid #e2e8f0', borderRadius: 4, backgroundColor: '#f8fafc' }}>
        <select
          value={newAuthor}
          onChange={(e) => setNewAuthor(e.target.value)}
          style={{ width: '100%', fontSize: 10, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 4, backgroundColor: '#fff' }}
        >
          <option value="">{t('adr.person.searchEmployee')}</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
        <textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder={t('adr.comment.placeholder')}
          style={{ width: '100%', fontSize: 11, padding: 6, border: '1px solid #d1d5db', borderRadius: 4, minHeight: 50, resize: 'vertical', backgroundColor: '#fff' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={handleSubmit}
            disabled={!newText.trim() || !newAuthor}
            style={{
              fontSize: 10, padding: '3px 10px', border: 'none', borderRadius: 4,
              background: '#3b82f6', color: '#fff', cursor: 'pointer',
              opacity: (!newText.trim() || !newAuthor) ? 0.4 : 1,
            }}
          >
            {t('adr.comment.addComment')}
          </button>
        </div>
      </div>
    </div>
  );
});
